import { useSeo } from '@/lib/useSeo'
import { PageHeader, Prose } from '@/components/public/PageHeader'

export function Accessibility() {
  useSeo({
    title: 'Accessibility statement',
    description:
      'VICE is committed to WCAG 2.1 AA and Section 508 compliance. Keyboard operability, screen reader support, sufficient color contrast, and responsive design are baseline requirements.',
  })
  return (
    <>
      <PageHeader
        eyebrow="Accessibility"
        title="Accessible by default."
        lede="VICE targets WCAG 2.1 AA and Section 508 on every user-facing surface."
      />
      <Prose>
        <h2>Our commitment</h2>
        <p>
          VICE is designed and continuously tested against the <strong>Web Content Accessibility
          Guidelines (WCAG) 2.1 Level AA</strong> and <strong>Section 508</strong> of the
          Rehabilitation Act. Accessibility is a baseline, not a feature.
        </p>

        <h2>What that means in practice</h2>
        <ul>
          <li>
            <strong>Keyboard operability.</strong> Every interactive element is reachable and
            operable via keyboard alone. Focus order follows visual order. Focus indicators are
            always visible.
          </li>
          <li>
            <strong>Screen-reader support.</strong> Semantic HTML throughout, ARIA landmarks on
            every page, live regions for status updates, text alternatives for all meaningful
            imagery.
          </li>
          <li>
            <strong>Color contrast.</strong> All token pairs meet at least 4.5:1 for body text and
            3:1 for large text.
          </li>
          <li>
            <strong>No color-only signals.</strong> Status and errors are paired with icons and
            text labels.
          </li>
          <li>
            <strong>Responsive design.</strong> Layouts reflow cleanly from 320px upward.
          </li>
          <li>
            <strong>Touch targets.</strong> 44×44px minimum on mobile surfaces.
          </li>
          <li>
            <strong>Reduced motion.</strong> Non-essential animation is disabled when users set the
            system preference.
          </li>
          <li>
            <strong>Skip link.</strong> Every page includes a &ldquo;Skip to main content&rdquo;
            shortcut.
          </li>
        </ul>

        <h2>How we test</h2>
        <ul>
          <li>
            <strong>Automated scans:</strong> <code>@axe-core/cli</code> runs on every pull request
            against every public URL.
          </li>
          <li>
            <strong>Manual keyboard pass:</strong> every new page is validated for tab order, focus
            visibility, and screen-reader announcements before merge.
          </li>
          <li>
            <strong>Lighthouse CI</strong> performance and accessibility scores tracked over time.
          </li>
        </ul>

        <h2>Feedback</h2>
        <p>
          Found a barrier? Please{' '}
          <a href="/contact">contact us</a> or email{' '}
          <a href="mailto:a11y@metaphase.tech">a11y@metaphase.tech</a>. We&apos;ll acknowledge
          receipt within 2 business days and prioritize the fix.
        </p>

        <h2>Standards we reference</h2>
        <ul>
          <li>
            <a href="https://www.w3.org/WAI/WCAG21/quickref/" target="_blank" rel="noreferrer">
              WCAG 2.1 — Quick Reference
            </a>
          </li>
          <li>
            <a href="https://www.section508.gov/" target="_blank" rel="noreferrer">
              Section 508 — section508.gov
            </a>
          </li>
          <li>
            <a href="https://designsystem.digital.gov/" target="_blank" rel="noreferrer">
              U.S. Web Design System
            </a>
          </li>
        </ul>
      </Prose>
    </>
  )
}
